import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { CLASSIFICATION_EMBEDDING_STATE, STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * One classification file stored under a scheduled job's prefix in the tenant's S3 bucket. There is
 * no database row behind this — the backend derives every field from S3 on each read, so the two
 * timestamps are the bucket's own.
 */
export type ClassificationFile = {
  filename: string;
  size_bytes: number;
  added_at: string;
  last_modified_at: string;
};

export type ClassificationEmbeddingState =
  (typeof CLASSIFICATION_EMBEDDING_STATE)[keyof typeof CLASSIFICATION_EMBEDDING_STATE];

/**
 * Reference data is owned by one **classification job**, not by the domain. A domain can hold
 * several classification jobs, each classifying into its own label tree, so everything here is
 * keyed by `scheduled_job_id`.
 *
 * The embedding fields arrive with the files rather than from a status call of their own: the
 * backend derives both from one listing of the job's prefix, so a second endpoint only meant
 * reading the same keys twice on every open, upload, delete and poll tick.
 *
 * `state` covers the whole example set rather than one file at a time — every vector lives in a
 * single S3 document, so there is nothing per-file to compare against.
 */
export type JobClassificationAssets = {
  scheduled_job_id: number;
  /** Null until a taxonomy is uploaded; a job has at most one, stored at a fixed key. */
  taxonomy: ClassificationFile | null;
  examples: ClassificationFile[];
  state: ClassificationEmbeddingState;
  /** When the embeddings document was last written; null when it does not exist yet. */
  embedded_at: string | null;
  is_running: boolean;
  /**
   * `proposed_taxonomy.json` — categories runs asked to add because nothing in the taxonomy fitted
   * a case. An output rather than an upload, so it has no upload or delete action of its own, and
   * it is null until a run has proposed something.
   */
  proposals: ClassificationFile | null;
};

export type UploadClassificationFilePayload = {
  scheduledJobId: number;
  file: File;
};

export type DeleteClassificationExamplePayload = {
  scheduledJobId: number;
  filename: string;
};

export type JobClassificationState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  assetsByJobId: Record<number, JobClassificationAssets>;
  /** Why the last embedding start failed, per job (already running, missing config, ...). */
  startErrorByJobId: Record<number, string | null>;
  startingJobIds: number[];
};

export const jobClassificationInitialState: JobClassificationState = {
  status: STATUS.IDLE,
  error: null,
  assetsByJobId: {},
  startErrorByJobId: {},
  startingJobIds: [],
};

const KNOWN_STATES: readonly string[] = Object.values(CLASSIFICATION_EMBEDDING_STATE);

/** Spelled once so the six thunks below cannot drift from each other or from the router. */
function classificationUrl(scheduledJobId: number, suffix = ""): string {
  return `/v1/scheduled-jobs/${scheduledJobId}/classification${suffix}`;
}

function isClassificationFile(value: unknown): value is ClassificationFile {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.filename === "string" &&
    typeof row.size_bytes === "number" &&
    typeof row.added_at === "string" &&
    typeof row.last_modified_at === "string"
  );
}

function parseAssetsResponse(data: unknown): JobClassificationAssets | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const row = data as Record<string, unknown>;
  if (typeof row.scheduled_job_id !== "number") {
    return null;
  }
  if (row.taxonomy !== null && !isClassificationFile(row.taxonomy)) {
    return null;
  }
  if (!Array.isArray(row.examples) || !row.examples.every(isClassificationFile)) {
    return null;
  }
  if (typeof row.is_running !== "boolean") {
    return null;
  }
  // Unlike the Bedrock document statuses, this vocabulary is closed and defined by our own
  // backend, so an unrecognised value is a contract mismatch rather than something to display.
  if (typeof row.state !== "string" || !KNOWN_STATES.includes(row.state)) {
    return null;
  }
  if (row.embedded_at !== null && typeof row.embedded_at !== "string") {
    return null;
  }
  // Absent as well as null: a backend older than this field sends no key, and rejecting the whole
  // response over it would blank a section that is otherwise perfectly readable.
  if (
    row.proposals !== null &&
    row.proposals !== undefined &&
    !isClassificationFile(row.proposals)
  ) {
    return null;
  }
  return { ...(data as JobClassificationAssets), proposals: row.proposals ?? null };
}

function sortByFilename(files: ClassificationFile[]): ClassificationFile[] {
  return [...files].sort((left, right) =>
    left.filename.localeCompare(right.filename, undefined, { sensitivity: "base" }),
  );
}

/**
 * An empty snapshot, so a reducer can record an upload for a job never fetched.
 *
 * The embedding fields take their "nothing here yet" values; the next read replaces the whole
 * snapshot with the bucket's own answer.
 */
function emptyAssets(scheduledJobId: number): JobClassificationAssets {
  return {
    scheduled_job_id: scheduledJobId,
    taxonomy: null,
    examples: [],
    state: CLASSIFICATION_EMBEDDING_STATE.NO_EXAMPLES,
    embedded_at: null,
    is_running: false,
    proposals: null,
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Request failed";
}

export const fetchJobClassificationAssets = createAsyncThunk<
  JobClassificationAssets,
  number,
  { rejectValue: string }
>("jobClassification/fetch", async (scheduledJobId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(classificationUrl(scheduledJobId), { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const assets = parseAssetsResponse(data);
    if (!assets) {
      return rejectWithValue("Unexpected response shape");
    }
    return assets;
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

/**
 * Replaces the job's taxonomy. PUT rather than POST because a job has exactly one, stored at a
 * fixed key — uploading a second one overwrites the first rather than adding to a list.
 */
export const uploadJobTaxonomy = createAsyncThunk<
  { scheduledJobId: number; taxonomy: ClassificationFile },
  UploadClassificationFilePayload,
  { rejectValue: string }
>("jobClassification/uploadTaxonomy", async ({ scheduledJobId, file }, { rejectWithValue }) => {
  try {
    const body = new FormData();
    body.append("file", file, file.name);
    // No explicit Content-Type: the browser has to set the multipart boundary itself.
    const res = await apiFetch(classificationUrl(scheduledJobId, "/taxonomy"), {
      method: "PUT",
      body,
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isClassificationFile(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { scheduledJobId, taxonomy: data };
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

/**
 * Uploads exactly one examples file. Callers upload a batch by dispatching this per file, so a file
 * the server rejects fails in isolation and its siblings still land.
 */
export const uploadJobClassificationExample = createAsyncThunk<
  { scheduledJobId: number; example: ClassificationFile },
  UploadClassificationFilePayload,
  { rejectValue: string }
>("jobClassification/uploadExample", async ({ scheduledJobId, file }, { rejectWithValue }) => {
  try {
    const body = new FormData();
    body.append("file", file, file.name);
    const res = await apiFetch(classificationUrl(scheduledJobId, "/examples"), {
      method: "POST",
      body,
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isClassificationFile(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { scheduledJobId, example: data };
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

export const deleteJobTaxonomy = createAsyncThunk<number, number, { rejectValue: string }>(
  "jobClassification/deleteTaxonomy",
  async (scheduledJobId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(classificationUrl(scheduledJobId, "/taxonomy"), {
        method: "DELETE",
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      return scheduledJobId;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const deleteJobClassificationExample = createAsyncThunk<
  DeleteClassificationExamplePayload,
  DeleteClassificationExamplePayload,
  { rejectValue: string }
>("jobClassification/deleteExample", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch(
      classificationUrl(
        payload.scheduledJobId,
        `/examples/${encodeURIComponent(payload.filename)}`,
      ),
      { method: "DELETE" },
    );
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return payload;
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

/**
 * Kicks off the embedding rebuild for this job's examples.
 *
 * Returns as soon as the run is enqueued — a full pass is tens of seconds of embedding calls, so
 * progress is read by re-fetching the assets, which carry `state` and `is_running`.
 */
export const startJobClassificationEmbedding = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("jobClassification/startEmbedding", async (scheduledJobId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(classificationUrl(scheduledJobId, "/embeddings"), {
      method: "POST",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return scheduledJobId;
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

const jobClassificationSlice = createSlice({
  name: "jobClassification",
  initialState: jobClassificationInitialState,
  reducers: {
    clearJobClassificationEmbeddingStartError(state, action: { payload: number }) {
      state.startErrorByJobId[action.payload] = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchJobClassificationAssets.pending, (state, action) => {
        // Only the first read shows a spinner; a poll refreshing an already-loaded snapshot must
        // not flip the section back into its loading state.
        if (state.assetsByJobId[action.meta.arg] === undefined) {
          state.status = STATUS.LOADING;
        }
        state.error = null;
      })
      .addCase(fetchJobClassificationAssets.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.error = null;
        state.assetsByJobId[action.payload.scheduled_job_id] = action.payload;
      })
      .addCase(fetchJobClassificationAssets.rejected, (state, action) => {
        state.error = action.payload ?? "Request failed";
        // `failed` is reserved for a read that left the section with nothing to render. A poll
        // that fails over an already-loaded snapshot records the error — which is what stops the
        // poll — without replacing readable content with an alert.
        if (state.assetsByJobId[action.meta.arg] === undefined) {
          state.status = STATUS.FAILED;
        }
      })
      .addCase(uploadJobTaxonomy.fulfilled, (state, action) => {
        const { scheduledJobId, taxonomy } = action.payload;
        const existing = state.assetsByJobId[scheduledJobId] ?? emptyAssets(scheduledJobId);
        state.assetsByJobId[scheduledJobId] = { ...existing, taxonomy };
      })
      .addCase(uploadJobClassificationExample.fulfilled, (state, action) => {
        const { scheduledJobId, example } = action.payload;
        const existing = state.assetsByJobId[scheduledJobId] ?? emptyAssets(scheduledJobId);
        // Replacing an existing name keeps its `added_at`; only its size and `last_modified_at`
        // move, which mirrors what S3 just did to the object.
        const withoutReplaced = existing.examples.filter(
          (row) => row.filename !== example.filename,
        );
        state.assetsByJobId[scheduledJobId] = {
          ...existing,
          examples: sortByFilename([...withoutReplaced, example]),
        };
      })
      .addCase(deleteJobTaxonomy.fulfilled, (state, action) => {
        const existing = state.assetsByJobId[action.payload];
        if (!existing) {
          return;
        }
        state.assetsByJobId[action.payload] = { ...existing, taxonomy: null };
      })
      .addCase(deleteJobClassificationExample.fulfilled, (state, action) => {
        const { scheduledJobId, filename } = action.payload;
        const existing = state.assetsByJobId[scheduledJobId];
        if (!existing) {
          return;
        }
        state.assetsByJobId[scheduledJobId] = {
          ...existing,
          examples: existing.examples.filter((row) => row.filename !== filename),
        };
      })
      .addCase(startJobClassificationEmbedding.pending, (state, action) => {
        const scheduledJobId = action.meta.arg;
        state.startErrorByJobId[scheduledJobId] = null;
        if (!state.startingJobIds.includes(scheduledJobId)) {
          state.startingJobIds.push(scheduledJobId);
        }
      })
      .addCase(startJobClassificationEmbedding.fulfilled, (state, action) => {
        const scheduledJobId = action.payload;
        state.startingJobIds = state.startingJobIds.filter((id) => id !== scheduledJobId);
        // Optimistically mark the job busy so the button disables and polling begins on the next
        // tick, instead of waiting a full poll interval for the backend to say so.
        const existing = state.assetsByJobId[scheduledJobId];
        if (existing) {
          state.assetsByJobId[scheduledJobId] = { ...existing, is_running: true };
        }
      })
      .addCase(startJobClassificationEmbedding.rejected, (state, action) => {
        const scheduledJobId = action.meta.arg;
        state.startingJobIds = state.startingJobIds.filter((id) => id !== scheduledJobId);
        state.startErrorByJobId[scheduledJobId] = action.payload ?? "Request failed";
      });
  },
});

export const { clearJobClassificationEmbeddingStartError } = jobClassificationSlice.actions;

export const jobClassificationReducer = jobClassificationSlice.reducer;

export function selectJobClassificationAssets(scheduledJobId: number | null) {
  return (state: RootState): JobClassificationAssets | null =>
    scheduledJobId === null
      ? null
      : (state.jobClassification.assetsByJobId[scheduledJobId] ?? null);
}

export const selectJobClassificationStatus = (state: RootState) => state.jobClassification.status;

export const selectJobClassificationError = (state: RootState) => state.jobClassification.error;

export function selectJobClassificationEmbeddingStartError(scheduledJobId: number | null) {
  return (state: RootState): string | null =>
    scheduledJobId === null
      ? null
      : (state.jobClassification.startErrorByJobId[scheduledJobId] ?? null);
}

export function selectIsJobClassificationEmbeddingStarting(scheduledJobId: number | null) {
  return (state: RootState): boolean =>
    scheduledJobId === null
      ? false
      : state.jobClassification.startingJobIds.includes(scheduledJobId);
}
